import * as React from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Download, FileText, Loader2, Trash2, TriangleAlert, Upload } from "lucide-react"

import { DataState } from "@/components/DataState"
import { PageHeader } from "@/components/PageHeader"
import { WorkPanel } from "@/components/WorkPanel"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { useToast } from "@/components/ui/use-toast"
import { useAuth, useRbac } from "@/lib/auth"
import { formatDate } from "@/lib/format"
import { t } from "@/lib/i18n"
import { cn } from "@/lib/utils"
import { onWriteError } from "@/lib/writeErrors"

import {
  archivarDeclaracion,
  darDeBajaDocumento,
  descargarDelExpediente,
  fetchDeclaracionesPresentadas,
  fetchDocumentos,
  fetchTiposDeDocumento,
  subirDocumento,
} from "@/routes/expediente/api"
import { fetchSociedades } from "@/routes/masters/api"

/**
 * EL EXPEDIENTE: los papeles de la empresa, y lo que se presentó de verdad.
 *
 * Cesar, punto 9 de la cuarta tanda: «podríamos crear una hoja de Documentos
 * Finales (y ahí que la gente cargue ficha RUC, consolidación de empresa, DNI
 * representante legal, etc) para que cualquiera pueda acceder cuando tiene que
 * enviarlo para alta de clientes o algo».
 *
 * ===========================================================================
 * LO QUE HACE ÚTIL ESTA PANTALLA ES QUIÉN PUEDE ENTRAR, NO QUÉ GUARDA
 * ===========================================================================
 *
 * Descargar basta con permiso de LECTURA. Es el punto entero: quien necesita la
 * ficha RUC para dar de alta a un cliente es comercial o administración, no el
 * contador. Si bajarla exigiera permiso de edición, habría que pedírsela a
 * alguien — que es exactamente el paso que esto viene a quitar. Subir y dar de
 * baja sí exigen `edit`: un documento de identidad de la empresa que cualquiera
 * puede sustituir es uno del que nadie se puede fiar.
 *
 * ---------------------------------------------------------------------------
 * DOS BLOQUES, Y NO SON LO MISMO AUNQUE LOS DOS SEAN PDF
 *
 * Arriba, **los papeles de la empresa**: cuelgan de la sociedad (o de ninguna, y
 * entonces valen para todas) y lo que tienen es vigencia.
 *
 * Abajo, **las declaraciones presentadas**: cuelgan de un período y un
 * formulario. No es lo mismo que lo que el sistema ARMA en Declaraciones — eso
 * sale de los datos de hoy y cambia si alguien corrige un comprobante; esto es
 * lo que el fisco tiene. Se dice en la pantalla, porque es la confusión cara.
 */
export function ExpedientePage() {
  const { activeTenantId, activeEntityId } = useAuth()

  const documentos = useQuery({
    queryKey: ["expediente-documentos", activeTenantId, activeEntityId],
    queryFn: () => fetchDocumentos(activeEntityId),
    enabled: Boolean(activeTenantId),
  })
  const declaraciones = useQuery({
    queryKey: ["expediente-declaraciones", activeTenantId, activeEntityId],
    queryFn: () => fetchDeclaracionesPresentadas(activeEntityId),
    enabled: Boolean(activeTenantId),
  })

  const vencidos = documentos.data?.vencidos ?? 0

  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow="Expediente"
        title={t("Documentos de la empresa")}
        description={t("Los papeles que te piden para darte de alta como proveedor, y las declaraciones tal como se presentaron. Cualquiera con acceso de lectura puede descargarlos: de eso se trata.")}
      />

      {/*
        EL AVISO DE VENCIDOS VA ARRIBA Y SÓLO SI HAY. Un documento caducado se ve
        igual que uno vigente en una lista, y mandar a un cliente una vigencia de
        poder vencida es exactamente el error que esta pantalla tiene que evitar.
      */}
      {vencidos > 0 ? (
        <p className="flex items-center gap-1.5 rounded-md border border-warning/40 bg-warning-soft px-2.5 py-2 text-2xs leading-5">
          <TriangleAlert className="h-3.5 w-3.5 shrink-0 text-warning" aria-hidden="true" />
          <span>
            <strong className="font-medium">{vencidos}</strong>{" "}
            {t("documento(s) con la vigencia pasada. Siguen aquí porque a veces se piden igual, pero conviene reemplazarlos antes de mandarlos.")}
          </span>
        </p>
      ) : null}

      <AltaDeDocumento />

      <WorkPanel
        title={t("Los papeles de la empresa")}
        meta={
          <span className="text-2xs text-muted-foreground">
            {t("Ficha fiscal, constitución, poderes, representante legal…")}
          </span>
        }
        footnote={documentos.data?.nota}
      >
        <DataState
          loading={documentos.isLoading}
          error={documentos.error}
          empty={(documentos.data?.documentos ?? []).length === 0}
          emptyMessage={t("Todavía no hay ningún documento archivado.")}
          emptyHint={t("Sube la ficha fiscal y la escritura de constitución: son los dos que piden siempre al dar de alta a un proveedor.")}
          onRetry={() => void documentos.refetch()}
          rows={4}
        >
          <ul className="divide-y">
            {(documentos.data?.documentos ?? []).map((d) => (
              <li key={d.id} className="flex flex-wrap items-center gap-2 px-2.5 py-2">
                <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-baseline gap-x-2 text-xs">
                    <span className="font-medium">{d.nombre}</span>
                    <Badge variant="outline">{d.tipo_nombre}</Badge>
                    {d.vencido ? (
                      <Badge variant="warning-soft" estado>
                        <TriangleAlert />
                        {t("vigencia pasada")}
                      </Badge>
                    ) : null}
                  </p>
                  <p className="text-2xs text-muted-foreground">
                    {d.alcance}
                    {d.vigente_hasta ? ` · ${t("vigente hasta")} ${formatDate(d.vigente_hasta)}` : ""}
                    {` · ${d.nombre_archivo}`}
                  </p>
                </div>
                <Button size="xs" variant="outline"
                        onClick={() => void descargarDelExpediente("documento", d.id, d.nombre_archivo)}>
                  <Download />
                  {t("Descargar")}
                </Button>
                <BotonDeBaja id={d.id} nombre={d.nombre} />
              </li>
            ))}
          </ul>
        </DataState>
      </WorkPanel>

      <AltaDeDeclaracion />

      <WorkPanel
        title={t("Las declaraciones tal como se presentaron")}
        meta={
          <span className="text-2xs text-muted-foreground">
            {t("El archivo que tiene el fisco, no el que el sistema arma hoy")}
          </span>
        }
        footnote={declaraciones.data?.nota}
      >
        <DataState
          loading={declaraciones.isLoading}
          error={declaraciones.error}
          empty={(declaraciones.data?.declaraciones ?? []).length === 0}
          emptyMessage={t("Todavía no se ha archivado ninguna declaración presentada.")}
          emptyHint={t("Archiva arriba el PDF que devolvió el fisco. Lo que el sistema arma y lo que se presentó pueden diferir —se presentó antes de cargar un comprobante, el contador ajustó una casilla, es una rectificatoria—, y lo que vale en una fiscalización es lo segundo.")}
          onRetry={() => void declaraciones.refetch()}
          rows={3}
        >
          <ul className="divide-y">
            {(declaraciones.data?.declaraciones ?? []).map((d) => (
              <li key={d.id} className="flex flex-wrap items-center gap-2 px-2.5 py-2">
                <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-baseline gap-x-2 text-xs">
                    <span className="code font-medium">{d.codigo}</span>
                    <span className="text-muted-foreground">
                      {d.anio}
                      {d.mes ? `-${String(d.mes).padStart(2, "0")}` : ""}
                    </span>
                    <Badge variant="outline">{d.razon_social}</Badge>
                    {d.numero_orden ? (
                      <span className="code text-2xs text-muted-foreground">
                        {t("orden")} {d.numero_orden}
                      </span>
                    ) : null}
                  </p>
                  <p className="text-2xs text-muted-foreground">
                    {t("presentada el")} {formatDate(d.presentada_en)} · {d.nombre_archivo}
                  </p>
                </div>
                <Button size="xs" variant="outline"
                        onClick={() => void descargarDelExpediente("declaracion", d.id, d.nombre_archivo)}>
                  <Download />
                  {t("Descargar")}
                </Button>
              </li>
            ))}
          </ul>
        </DataState>
      </WorkPanel>
    </div>
  )
}

/** Dar de baja: exige motivo, y no borra. Ver el router. */
function BotonDeBaja({ id, nombre }: { id: string; nombre: string }) {
  const { can } = useRbac()
  const { toast } = useToast()
  const qc = useQueryClient()
  const baja = useMutation({
    mutationFn: () => darDeBajaDocumento(id, `Dado de baja desde el expediente: ${nombre}`),
    onSuccess: () => {
      toast({ title: t("Dado de baja"), description: t("Deja de aparecer; la fila se queda.") })
      void qc.invalidateQueries({ queryKey: ["expediente-documentos"] })
    },
    onError: onWriteError("el documento", "dar de baja"),
  })
  if (!can("edit")) return null
  return (
    <Button size="iconSm" variant="ghost" aria-label={t("Dar de baja")}
            disabled={baja.isPending} onClick={() => baja.mutate()}>
      <Trash2 />
    </Button>
  )
}

/* ==========================================================================
   SUBIR UN PAPEL

   La sociedad se deja VACÍA por defecto, y eso significa «vale para todas». Es
   lo correcto para la mitad de los casos —un poder general, el documento del
   representante— y obligar a elegir una haría subir el mismo PDF cuatro veces.
   ========================================================================== */

function AltaDeDocumento() {
  const { can } = useRbac()
  const { toast } = useToast()
  const qc = useQueryClient()
  const entrada = React.useRef<HTMLInputElement>(null)

  const tipos = useQuery({ queryKey: ["expediente-tipos"], queryFn: fetchTiposDeDocumento })
  const sociedades = useQuery({ queryKey: ["sociedades"], queryFn: fetchSociedades })

  const [archivo, setArchivo] = React.useState<File | null>(null)
  const [tipo, setTipo] = React.useState("identificacion_fiscal")
  const [nombre, setNombre] = React.useState("")
  const [entityId, setEntityId] = React.useState("")
  const [vigencia, setVigencia] = React.useState("")
  const [nota, setNota] = React.useState("")

  const subir = useMutation({
    mutationFn: () =>
      subirDocumento({
        archivo: archivo as File,
        tipo,
        nombre: nombre.trim(),
        entityId: entityId || null,
        vigenteHasta: vigencia || null,
        nota: nota.trim() || null,
      }),
    onSuccess: (r) => {
      toast({ title: `${r.nombre} archivado`, description: t("Ya se puede descargar.") })
      setArchivo(null); setNombre(""); setVigencia(""); setNota("")
      if (entrada.current) entrada.current.value = ""
      void qc.invalidateQueries({ queryKey: ["expediente-documentos"] })
    },
    onError: onWriteError("el documento", "archivar"),
  })

  if (!can("edit")) return null

  const listo = Boolean(archivo) && nombre.trim().length >= 2 && Boolean(tipo)

  return (
    <WorkPanel title={t("Archivar un documento")} bodyClassName="p-2.5">
      <div className="space-y-2.5">
        <input
          ref={entrada}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.webp,.xml,.zip,application/pdf,image/*"
          className="sr-only"
          onChange={(e) => {
            const f = e.target.files?.[0] ?? null
            setArchivo(f)
            // EL NOMBRE SE PROPONE DEL ARCHIVO. Es lo que alguien iba a teclear
            // igual, y un campo obligatorio vacío al lado de un archivo ya
            // elegido es la fricción que hace abandonar la subida.
            if (f && !nombre.trim()) setNombre(f.name.replace(/\.[^.]+$/, ""))
          }}
        />

        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => entrada.current?.click()}>
            <Upload />
            {archivo ? t("Cambiar el archivo") : t("Elegir un archivo")}
          </Button>
          {archivo ? (
            <span className="truncate text-2xs text-muted-foreground">{archivo.name}</span>
          ) : (
            <span className="text-2xs text-muted-foreground">
              {t("PDF, imagen, XML o ZIP. Hasta 25 MB.")}
            </span>
          )}
        </div>

        <div className="grid gap-2 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="ex-tipo">{t("Qué papel es")}</Label>
            <Select value={tipo} onValueChange={setTipo}>
              <SelectTrigger id="ex-tipo" className="text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(tipos.data?.tipos ?? []).map((x) => (
                  <SelectItem key={x.id} value={x.id}>{x.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="ex-nombre">{t("Cómo llamarlo")}</Label>
            <Input id="ex-nombre" value={nombre} onChange={(e) => setNombre(e.target.value)}
                   placeholder={t("Ficha RUC — SIETE AGENCY S.A.C.")} className="text-xs" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="ex-soc">{t("De qué sociedad")}</Label>
            <Select value={entityId} onValueChange={setEntityId}>
              <SelectTrigger id="ex-soc" className="text-xs">
                <SelectValue placeholder={t("Vale para todas")} />
              </SelectTrigger>
              <SelectContent>
                {(sociedades.data ?? []).map((x) => (
                  <SelectItem key={x.id} value={x.id}>{x.razon_social} · {x.pais}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="ex-vig">{t("Vigente hasta")}</Label>
            <Input id="ex-vig" type="date" value={vigencia}
                   onChange={(e) => setVigencia(e.target.value)} className="text-xs" />
          </div>
        </div>

        <div className="space-y-1">
          <Label htmlFor="ex-nota">{t("Nota")}</Label>
          <Textarea id="ex-nota" rows={2} value={nota} onChange={(e) => setNota(e.target.value)}
                    placeholder={t("A quién se le suele mandar, qué versión es…")}
                    className="text-xs" />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" disabled={!listo || subir.isPending} onClick={() => subir.mutate()}>
            {subir.isPending ? <Loader2 className="animate-spin" /> : <Upload />}
            {subir.isPending ? t("Archivando…") : t("Archivarlo")}
          </Button>
          {/*
            LA VIGENCIA SE DEJA VACÍA SI NO CADUCA, y se dice. Inventar una fecha
            haría que el sistema avisara de documentos vencidos que no vencen —o
            peor, que se callara sobre uno que sí.
          */}
          <span className={cn("text-2xs text-muted-foreground")}>
            {t("La vigencia sólo si caduca: una ficha fiscal no caduca y un poder sí.")}
          </span>
        </div>
      </div>
    </WorkPanel>
  )
}


/* ==========================================================================
   ARCHIVAR UNA DECLARACIÓN PRESENTADA

   NO COMPRUEBA QUE CUADRE con lo que el sistema armaría, y es deliberado.
   Pueden diferir por motivos legítimos —se presentó antes de terminar de cargar,
   el contador ajustó una casilla a mano, es una rectificatoria— y rechazar el
   archivo por eso dejaría fuera justo el caso donde más falta hace tener la
   evidencia.

   LAS RECTIFICATORIAS SE APILAN: no hay restricción única por (sociedad,
   período, código). Con una, alguien habría borrado la original para subir la
   rectificada — y la original es la que no se puede perder.
   ========================================================================== */

function AltaDeDeclaracion() {
  const { can } = useRbac()
  const { toast } = useToast()
  const qc = useQueryClient()
  const entrada = React.useRef<HTMLInputElement>(null)
  const [abierto, setAbierto] = React.useState(false)

  const sociedades = useQuery({ queryKey: ["sociedades"], queryFn: fetchSociedades })

  const [archivo, setArchivo] = React.useState<File | null>(null)
  const [entityId, setEntityId] = React.useState("")
  const [codigo, setCodigo] = React.useState("")
  const [anio, setAnio] = React.useState(String(new Date().getFullYear()))
  const [mes, setMes] = React.useState("")
  const [presentada, setPresentada] = React.useState("")
  const [orden, setOrden] = React.useState("")
  const [importe, setImporte] = React.useState("")

  const archivar = useMutation({
    mutationFn: () =>
      archivarDeclaracion({
        archivo: archivo as File,
        entityId,
        anio: Number(anio),
        mes: mes ? Number(mes) : null,
        codigo: codigo.trim(),
        presentadaEn: presentada,
        numeroOrden: orden.trim() || null,
        importePagado: importe === "" ? null : Number(importe),
        moneda: null,
      }),
    onSuccess: (r) => {
      toast({ title: `${r.codigo} archivada`, description: r.siguiente })
      setArchivo(null); setCodigo(""); setMes(""); setPresentada(""); setOrden(""); setImporte("")
      if (entrada.current) entrada.current.value = ""
      setAbierto(false)
      void qc.invalidateQueries({ queryKey: ["expediente-declaraciones"] })
    },
    onError: onWriteError("la declaración", "archivar"),
  })

  if (!can("edit")) return null

  const listo =
    Boolean(archivo) && Boolean(entityId) && codigo.trim().length >= 2
    && Boolean(presentada) && /^\d{4}$/.test(anio)

  if (!abierto) {
    return (
      <Button size="sm" variant="outline" onClick={() => setAbierto(true)}>
        <Upload />
        {t("Archivar una declaración presentada")}
      </Button>
    )
  }

  return (
    <WorkPanel title={t("Archivar una declaración presentada")} bodyClassName="p-2.5">
      <div className="space-y-2.5">
        <p className="text-2xs leading-5 text-muted-foreground">
          {t("El PDF o la constancia que devolvió el fisco. No tiene que cuadrar con lo que el sistema arma: si difieren, lo que vale es esto.")}
        </p>

        <input
          ref={entrada}
          type="file"
          accept=".pdf,.xml,.zip,.jpg,.png,application/pdf"
          className="sr-only"
          onChange={(e) => setArchivo(e.target.files?.[0] ?? null)}
        />
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => entrada.current?.click()}>
            <Upload />
            {archivo ? t("Cambiar el archivo") : t("Elegir el archivo")}
          </Button>
          {archivo ? (
            <span className="truncate text-2xs text-muted-foreground">{archivo.name}</span>
          ) : null}
        </div>

        <div className="grid gap-2 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="dp-soc">{t("De qué sociedad")}</Label>
            <Select value={entityId} onValueChange={setEntityId}>
              <SelectTrigger id="dp-soc" className="text-xs">
                <SelectValue placeholder={t("Elige la sociedad")} />
              </SelectTrigger>
              <SelectContent>
                {(sociedades.data ?? []).map((x) => (
                  <SelectItem key={x.id} value={x.id}>{x.razon_social} · {x.pais}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="dp-cod">{t("Qué formulario")}</Label>
            <Input id="dp-cod" value={codigo} onChange={(e) => setCodigo(e.target.value)}
                   placeholder="PE.FV621" className="code text-xs" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="dp-anio">{t("Año del período")}</Label>
            <Input id="dp-anio" value={anio} inputMode="numeric"
                   onChange={(e) => setAnio(e.target.value)} className="num text-xs" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="dp-mes">{t("Mes del período")}</Label>
            <Input id="dp-mes" value={mes} inputMode="numeric"
                   onChange={(e) => setMes(e.target.value)}
                   placeholder={t("vacío si es anual")} className="num text-xs" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="dp-fecha">{t("Cuándo se presentó")}</Label>
            <Input id="dp-fecha" type="date" value={presentada}
                   onChange={(e) => setPresentada(e.target.value)} className="text-xs" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="dp-orden">{t("Número de orden")}</Label>
            <Input id="dp-orden" value={orden} onChange={(e) => setOrden(e.target.value)}
                   placeholder={t("el que devolvió el fisco")} className="code text-xs" />
          </div>
        </div>

        {/*
          EL PERÍODO ES EL DEL IMPUESTO, NO EL DEL TRÁMITE, y se dice: el IGV de
          marzo se presenta en abril, y guardar abril haría imposible contestar
          «qué presentamos de marzo» — que es la única pregunta que se hace.
        */}
        <p className="text-2xs leading-5 text-muted-foreground">
          {t("El año y el mes son los del PERÍODO que se declara, no los del día en que se presentó: el IGV de marzo se presenta en abril y se busca por marzo.")}
        </p>

        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" disabled={!listo || archivar.isPending}
                  onClick={() => archivar.mutate()}>
            {archivar.isPending ? <Loader2 className="animate-spin" /> : <Upload />}
            {archivar.isPending ? t("Archivando…") : t("Archivarla")}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setAbierto(false)}>
            {t("Cancelar")}
          </Button>
        </div>
      </div>
    </WorkPanel>
  )
}
